import React from "react";
import { useForm } from "react-hook-form";
import { X, Menu, Route } from "lucide-react";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";

interface AddMenuRequest {
  menu_name: string;
  route_path: string;
}

interface AddMenuFormProps {
  initialData?: {
    menu_name: string;
    route_path: string;
  } | null;
  isLoading?: boolean;
  onSubmit: (data: AddMenuRequest) => void;
  onCancel: () => void;
}

export const AddMenuForm: React.FC<AddMenuFormProps> = ({
  initialData = null,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddMenuRequest>({
    defaultValues: initialData || {
      menu_name: "",
      route_path: "",
    },
  });

  const onFormSubmit = (data: AddMenuRequest) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-md">
        <div className="bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Add New Menu</h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="text-slate-300 text-sm mb-4">
            Please fill in the menu details below.
          </div>

          <div className="space-y-4">
            <CustomInput
              label="Menu Name"
              type="text"
              placeholder="e.g., Dashboard, Users, Reports"
              register={register}
              name="menu_name"
              validation={{
                required: "Menu name is required",
                validate: (v: unknown) => {
                  const trimmed = String(v ?? "").trim();
                  if (trimmed.length === 0) return "Menu name cannot be empty";
                  if (trimmed.length < 2)
                    return "Menu name must be at least 2 characters";
                  return true;
                },
              }}
              error={errors.menu_name}
              disabled={isLoading}
              icon={<Menu className="w-5 h-5 text-slate-400" />}
            />

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">
                Route Path
              </label>
              <div className="relative">
                <Route className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  {...register("route_path", {
                    required: "Route path is required",
                    validate: (v: unknown) => {
                      const trimmed = String(v ?? "").trim();
                      if (trimmed.length === 0)
                        return "Route path cannot be empty";
                      if (!trimmed.startsWith("/"))
                        return "Route path must start with /";
                      if (trimmed.length < 2)
                        return "Route path must be at least 2 characters";
                      return true;
                    },
                  })}
                  type="text"
                  placeholder="e.g., /dashboard, /users, /reports"
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              {errors.route_path && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.route_path.message}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              className="flex-1"
            >
              {initialData ? "Update Menu" : "Add Menu"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
